import layoutHelpers from '@/layout/helpers.js'

export default function () {
  return {
    // Public url
    publicUrl: process.env.BASE_URL,

    // Layout helpers
    layoutHelpers,

    // Check for RTL layout
    get isRtlMode () {
      return document.documentElement.getAttribute('dir') === 'rtl' ||
             document.body.getAttribute('dir') === 'rtl'
    },

    // Check if IE
    get isIEMode () {
      return typeof document.documentMode === 'number'
    },

    // Check if IE10
    get isIE10Mode () {
      return this.isIEMode && document.documentMode === 10
    },

    // Layout navbar color
    get layoutNavbarBg () {
      return 'navbar-theme'
    },

    // Layout sidenav color
    get layoutSidenavBg () {
      return 'sidenav-theme'
    },

    // Layout footer color
    get layoutFooterBg () {
      return 'footer-theme'
    },

    get isMobile () {
      return window.innerWidth <= 480
    },

    // AI Chat Sidebar State
    aiChatSidebarVisible: false,
    aiChatSidebarWidth: 400,
    aiChatAdditionalMetadata: {},

    // AI Chat Sidebar Methods
    async showAiChatSidebar() {
      // Require AI consent before opening the AI chat sidebar
      if (typeof this.$msgBoxAIConsent === 'function') {
        const ok = await this.$msgBoxAIConsent();
        if (!ok) return;
      }

      this.aiChatSidebarVisible = true;
      // Auto-collapse left sidebar when AI chat opens
      if (!this.layoutHelpers.isCollapsed()) {
        this.layoutHelpers.setCollapsed(true);
      }
      // Save state to localStorage
      this.saveAiChatState();

      this.$root.analyticsEvent('Ai Chat - opened');
    },

    hideAiChatSidebar() {
      this.aiChatSidebarVisible = false;
      this.saveAiChatState();
    },

    async toggleAiChatSidebar() {
      if (this.aiChatSidebarVisible) {
        this.hideAiChatSidebar();
      } else {
        await this.showAiChatSidebar();
      }
    },

    // AI Chat State Persistence
    saveAiChatState() {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('aiChatSidebarState', JSON.stringify({
          visible: this.aiChatSidebarVisible,
          width: this.aiChatSidebarWidth
        }));
      }
    },

    loadAiChatState() {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('aiChatSidebarState');
        if (saved) {
          try {
            const state = JSON.parse(saved);
            this.aiChatSidebarVisible = state.visible || false;
            this.aiChatSidebarWidth = state.width || 400;
          } catch (e) {
            // Ignore invalid JSON
          }
        }

        // Enforce consent: if consent hasn't been granted, do not show the sidebar
        if (typeof this.$hasAIConsent === 'function') {
          if (!this.$hasAIConsent()) {
            this.aiChatSidebarVisible = false;
          }
        }
      }
    },

    // AI Chat Metadata Management
    setAiChatMetadata(metadata = {}) {
      if ('formHash' in metadata && !metadata.formHash) {
        // console.warn('setAiChatMetadata -> there was a formHash key but empty, probably invalid reload..')
        return
      }
      if ((this.aiChatAdditionalMetadata?.formHash || metadata?.formHash) && this.aiChatAdditionalMetadata?.formHash !== metadata?.formHash) {
        setTimeout(e => document.body.dispatchEvent(new CustomEvent('aiFormHashChanged', { bubbles: true })), 100)
      }
      this.aiChatAdditionalMetadata = { ...metadata };
    },

    updateAiChatMetadata(metadata = {}) {
      if ('formHash' in metadata && !metadata.formHash) {
        // console.warn('setAiChatMetadata -> there was a formHash key but empty, probably invalid reload..')
        return
      }
      if ((this.aiChatAdditionalMetadata?.formHash || metadata?.formHash) && this.aiChatAdditionalMetadata?.formHash !== metadata?.formHash) {
        setTimeout(e => document.body.dispatchEvent(new CustomEvent('aiFormHashChanged', { bubbles: true })), 100)
      }
      this.aiChatAdditionalMetadata = { ...this.aiChatAdditionalMetadata, ...metadata };
    },

    clearAiChatMetadata() {
      setTimeout(e => document.body.dispatchEvent(new CustomEvent('aiFormHashChanged', { bubbles: true })), 100)
      this.aiChatAdditionalMetadata = {};
    },

    // Animate scrollTop
    scrollTop (to, duration, element = document.scrollingElement || document.documentElement) {
      if (element.scrollTop === to) return
      const start = element.scrollTop
      const change = to - start
      const startDate = +new Date()

      // t = current time; b = start value; c = change in value; d = duration
      const easeInOutQuad = (t, b, c, d) => {
        t /= d / 2
        if (t < 1) return c / 2 * t * t + b
        t--
        return -c / 2 * (t * (t - 2) - 1) + b
      }

      const animateScroll = () => {
        const currentDate = +new Date()
        const currentTime = currentDate - startDate
        element.scrollTop = parseInt(easeInOutQuad(currentTime, start, change, duration))
        if (currentTime < duration) {
          requestAnimationFrame(animateScroll)
        } else {
          element.scrollTop = to
        }
      }

      animateScroll()
    }
  }
}
